/**
 * app/screens/EditItemScreen.tsx
 * -------------------------------
 * What: Form for editing an existing item; mirrors AddItem with prefilled data.
 * Why: Supports updating item details, attributes, and associated images.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  TextInput as BaseTextInputOriginal,
  TouchableOpacity,
  StyleSheet,
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
  Modal,
  Image,
  SafeAreaView,
  StatusBar,
  Pressable,
  useWindowDimensions,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSubscription } from '../../lib/SubscriptionContext';
import { useAuth } from '../../lib/AuthContext';
import { supabase } from '../../lib/supabase';
import type { RootStackParamList } from '../types/navigation';
import { Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import { manipulateImage } from './ImageManipulation';
import * as FileSystem from 'expo-file-system';
import { getPlatform } from '../utils/platform';
import { BarCodeScanner } from 'expo-barcode-scanner';
import { usePaginatedItems } from '../../lib/DataContext';
import imageCompression from 'browser-image-compression';
import { useTheme } from '../../lib/ThemeContext';
import { CommonActions } from '@react-navigation/native';
import { DndContext, useSensors, useSensor, PointerSensor, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DraggableFlatList, { RenderItemParams } from 'react-native-draggable-flatlist';
import { useImageCache } from '../../lib/ImageCacheContext';
import ReceiptSelector, { Receipt } from '../components/ReceiptSelector';
import FormTextInput from '../components/FormTextInput';
import FormLabel from '../components/FormLabel';
import ThemedButton from '../components/ThemedButton';
import DynamicAttributes, { AttributeDefinition } from '../components/DynamicAttributes';

// Types
import type { RouteProp } from '@react-navigation/native';
type NavigationProp = NativeStackNavigationProp<RootStackParamList>;
type EditItemRouteProp = RouteProp<RootStackParamList, 'EditItem'>;
type Room = { id: string; name: string };
type Category = { id: string; name: string };
type Subcategory = { id: string; name: string };
type ItemImage = { id: string; url: string; order: number; item_id: string; created_at: string; };
interface Picture {
  id: string;
  uri?: string;
  url?: string;
  order: number;
  isDeleted?: boolean;
}

type ProcessedImage = {
  id: string;
  uri: string;
  url: string;
  order: number;
  isDeleted: boolean;
};

type FileWithPath = {
  uri: string;
  name: string;
  type: string;
  file_path: string;
};

type ItemDocument = {
  id: string;
  type: 'receipt' | 'warranty';
  file_path: string;
};

type ItemWithRelations = {
  id: string;
  name: string;
  description: string | null;
  serial_number: string | null;
  make: string | null;
  model: string | null;
  purchase_date: string | null;
  price: number | null;
  warranty_expiry: string | null;
  place_of_purchase: string | null;
  room: Room;
  subcategory?: { id: string; name: string; category: { id: string; name: string } };
  item_images: ItemImage[];
  item_documents: ItemDocument[];
};

function toPictureArray(arr: any[]): Picture[] {
  return arr.map((p) => ({ 
    id: typeof p.id === 'string' ? p.id : p.uri, 
    uri: p.uri, 
    order: p.order || 0 
  }));
}

// Helper to detect mobile web
const isMobileWeb = Platform.OS === 'web' && (typeof window !== 'undefined' ? window.innerWidth < 768 : false);
const imageSquareSize = Platform.OS === 'web' ? (isMobileWeb ? 90 : 100) : 90;

// Add SortableImage for WebImageGrid
const SortableImage = ({ picture, onDelete, size = 100 }: { picture: Picture; onDelete: (id: string) => void; size?: number }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: picture.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    width: `${size}px`,
    height: `${size}px`,
    position: 'relative',
    border: '1px solid #ccc',
    borderRadius: '4px',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <img 
        src={picture.uri} 
        alt={`Image ${picture.id}`} 
        style={{ 
          maxWidth: '100%', 
          maxHeight: '100%', 
          objectFit: 'contain',
          width: 'auto',
          height: 'auto'
        }} 
      />
      <button
        onClick={() => onDelete(picture.id)}
        style={{
          position: 'absolute',
          top: '5px',
          right: '5px',
          background: 'rgba(255, 0, 0, 0.7)',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '20px',
          height: '20px',
          cursor: 'pointer',
        }}
      >
        X
      </button>
    </div>
  );
};

// Stable, top-level Web image grid (no theme dependencies)
const WebImageGridComponent = React.memo(({ pictures, onReorder, onDelete, onAdd, size }: { pictures: Picture[]; onReorder: (pictures: Picture[]) => void; onDelete: (id: string) => void; onAdd: () => void; size: number }) => {
  const visiblePictures = React.useMemo(() => pictures.filter(pic => !pic.isDeleted), [pictures]);
  const [internalOrder, setInternalOrder] = React.useState<Picture[]>(visiblePictures);

  React.useEffect(() => {
    if (JSON.stringify(visiblePictures) !== JSON.stringify(internalOrder)) {
      setInternalOrder(visiblePictures);
    }
  }, [visiblePictures]);

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over) return;
    if (active.id !== over.id) {
      const oldIndex = internalOrder.findIndex((item) => item.id === active.id);
      const newIndex = internalOrder.findIndex((item) => item.id === over.id);
      const newOrder = arrayMove(internalOrder, oldIndex, newIndex);
      onReorder(newOrder);
    }
  };

  return (
    <DndContext
      sensors={useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={internalOrder.map((item: Picture) => item.id)} strategy={verticalListSortingStrategy}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {internalOrder.map((picture: Picture) => (
            <SortableImage key={picture.id} picture={picture} onDelete={onDelete} size={size} />
          ))}
          {internalOrder.length < 3 && (
            <button
              onClick={onAdd}
              style={{
                width: size,
                height: size,
                border: '2px dashed #007AFF',
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                color: '#007AFF',
                fontSize: 32,
                cursor: 'pointer',
                margin: 4,
              }}
              aria-label="Add Image"
            >
              +
            </button>
          )}
        </div>
      </SortableContext>
    </DndContext>
  );
});

type ThemeColors = { inputBackground: string; border: string; card: string; text: string };

// Stable, top-level Native image grid (theme passed via props)
const NativeImageGridComponent = React.memo(({ pictures, onReorder, onDelete, onAdd, size, theme }: { pictures: Picture[]; onReorder: (pictures: Picture[]) => void; onDelete: (id: string) => void; onAdd: () => void; size: number; theme: ThemeColors }) => {
  const renderItem = ({ item }: RenderItemParams<Picture>) => (
    <View style={{ 
      position: 'relative', 
      width: size, 
      height: size, 
      borderRadius: 8, 
      backgroundColor: theme.inputBackground, 
      borderWidth: 1, 
      borderColor: theme.border, 
      justifyContent: 'center', 
      alignItems: 'center',
      padding: 4
    }}>
      <Image 
        source={{ uri: item.uri }} 
        style={{ width: '100%', height: '100%', borderRadius: 6 }} 
        resizeMode="contain" 
      />
      <TouchableOpacity 
        style={{ position: 'absolute', top: -8, right: -8, backgroundColor: theme.card, borderRadius: 12 }} 
        onPress={() => onDelete(item.id)}
      >
        <Ionicons name="close-circle" size={24} color={theme.text} />
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
      <DraggableFlatList
        data={pictures}
        renderItem={renderItem}
        keyExtractor={(item: Picture) => item.id}
        onDragEnd={({ data }) => onReorder(data)}
        horizontal={true}
      />
      {pictures.length < 3 && (
        <TouchableOpacity
          onPress={onAdd}
          style={{
            width: size,
            height: size,
            borderWidth: 2,
            borderStyle: 'dashed',
            borderColor: '#007AFF',
            borderRadius: 8,
            justifyContent: 'center',
            alignItems: 'center',
            margin: 4,
          }}
          accessibilityRole="button"
          accessibilityLabel="Add Image"
        >
          <Text style={{ color: '#007AFF', fontSize: 32 }}>+</Text>
        </TouchableOpacity>
      )}
    </View>
  );
});

// Alias: internal JSX uses BaseTextInput; map it to wrapper.
const BaseTextInput = FormTextInput as unknown as typeof BaseTextInputOriginal;

export default function EditItemScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<EditItemRouteProp>();
  const { itemId, returnPage } = route.params;
  const { tier, refreshCounts, currency, dateFormat } = useSubscription();
  const { user } = useAuth();
  const { refresh } = usePaginatedItems();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [purchaseDate, setPurchaseDate] = useState<Date | null>(null);
  const [price, setPrice] = useState('');
  const [priceError, setPriceError] = useState('');
  const [warrantyExpiry, setWarrantyExpiry] = useState<Date | null>(null);
  const [placeOfPurchase, setPlaceOfPurchase] = useState('');
  const [receiptFile, setReceiptFile] = useState<FileWithPath | null>(null);
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null);
  const [showReceiptSelector, setShowReceiptSelector] = useState(false);
  const [currentReceiptId, setCurrentReceiptId] = useState<string | null>(null);
  const [warrantyFile, setWarrantyFile] = useState<FileWithPath | null>(null);
  const [loading, setLoading] = useState(true);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState<Subcategory | null>(null);
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [showSuggestCategoryModal, setShowSuggestCategoryModal] = useState(false);
  const [showSuggestSubcategoryModal, setShowSuggestSubcategoryModal] = useState(false);
  const [proposedCategoryName, setProposedCategoryName] = useState('');
  const [proposedSubcategoryName, setProposedSubcategoryName] = useState('');
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showWarrantyPicker, setShowWarrantyPicker] = useState(false);
  const [datePickerMode, setDatePickerMode] = useState<'purchase' | 'warranty'>('purchase');
  const [tempDate, setTempDate] = useState<Date>(new Date());
  const [pictures, setPictures] = useState<Picture[]>([]);
  const [showRoomRequiredModal, setShowRoomRequiredModal] = useState(false);
  const [showNameRequiredModal, setShowNameRequiredModal] = useState(false);
  const { width: windowWidth } = useWindowDimensions();
  const isSmallScreen = windowWidth < 768;
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [showScanner, setShowScanner] = useState(false);
  const { theme, themeType } = useTheme();
  const [purchaseDateError, setPurchaseDateError] = useState('');
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showWarrantyModal, setShowWarrantyModal] = useState(false);
  const scrollViewRef = useRef<ScrollView>(null);
  const nameFieldRef = useRef<any>(null);
  const roomFieldRef = useRef<any>(null);
  const imageFieldRef = useRef<any>(null);
  const priceFieldRef = useRef<any>(null);
  const categoryFieldRef = useRef<any>(null);
  const subcategoryFieldRef = useRef<any>(null);
  const [showImageLimitError, setShowImageLimitError] = useState(false);
  const currencySymbols: { [key: string]: string } = { GBP: '£', USD: '$', EUR: '€' };
  const [nameError, setNameError] = useState('');
  const [roomError, setRoomError] = useState('');
  const [showCategoryRequiredError, setShowCategoryRequiredError] = useState(false);
  const [showSubcategoryRequiredError, setShowSubcategoryRequiredError] = useState(false);
  const [showDeleteDocModal, setShowDeleteDocModal] = useState<{ type: 'receipt' | 'warranty' | null }>({ type: null });
  const [pendingDeleteDoc, setPendingDeleteDoc] = useState<null | (() => Promise<void>)>(null);
  const { clearItemCache } = useImageCache();
  const [originalImages, setOriginalImages] = useState<ItemImage[]>([]);
  // Advisory modal when place/date of purchase is missing before upload receipt
  const [showReceiptAdviceModal, setShowReceiptAdviceModal] = useState(false);
  const [receiptAdviceMessage, setReceiptAdviceMessage] = useState('');
  // Delete item modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  // Dynamic attributes
  const [attributeDefs, setAttributeDefs] = useState<AttributeDefinition[]>([]);
  const [dynAttributes, setDynAttributes] = useState<Record<string, any>>({});
  const hasDef = (key: string) => attributeDefs.some((d) => d.key === key);

  // Additional documents (user-named)
  const MAX_ADDITIONAL_DOCS = 5;
  const [existingAdditionalDocs, setExistingAdditionalDocs] = useState<Array<{ id: string; name: string | null; file_path: string }>>([]);
  const [renameDocModal, setRenameDocModal] = useState<{ id: string | null; name: string }>({ id: null, name: '' });
  const [pendingAdditionalDocs, setPendingAdditionalDocs] = useState<Array<{ uri: string; name: string; type: string }>>([]);
  const [showAddDocNameModal, setShowAddDocNameModal] = useState(false);
  const [pendingAdditionalDocFile, setPendingAdditionalDocFile] = useState<{ uri: string; name: string; type: string } | null>(null);
  const [pendingAdditionalDocName, setPendingAdditionalDocName] = useState('');

  const glowStyle = themeType === 'blueNeon'
    ? (Platform.OS === 'web'
        ? { boxShadow: `0 0 6px 1px ${theme.accent}` } as any
        : { shadowColor: theme.accent, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.5, shadowRadius: 6, elevation: 6 })
    : {};

  // Move reindexPictures before the functions that use it
  const reindexPictures = (pics: Picture[]): Picture[] => {
    return pics.map((pic, index) => ({
      ...pic,
      order: index
    }));
  };

  const handleDeletePicture = useCallback((index: number) => {
    const updatedPictures = pictures.filter((_, i) => i !== index);
    const reindexedPictures = reindexPictures(updatedPictures);
    setPictures(reindexedPictures);
  }, [pictures]);

  const fetchItem = useCallback(async () => {
    setLoading(true);
    try {
      const { data: item, error } = await supabase
        .from('items')
        .select(`
          *,
          room:rooms!items_room_id_fkey (
            id,
            name
          ),
          subcategory:item_subcategories!items_subcategory_id_fkey (
            id,
            name,
            category:item_categories (
              id,
              name
            )
          ),
          item_images (
            id,
            url,
            order
          ),
          item_documents (
            id,
            type,
            file_path,
            name
          )
        `)
        .eq('id', itemId)
        .single();

      if (error) throw error;

      if (item) {
        setName(item.name || '');
        setDescription(item.description || '');
        const attrs = (item as any).attributes || {};
        setSerialNumber(attrs.serial || '');
        setMake(attrs.make || '');
        setModel(attrs.model || '');
        setPurchaseDate(item.purchase_date ? new Date(item.purchase_date) : null);
        setPrice(item.price ? item.price.toString() : '');
        setWarrantyExpiry(item.warranty_expiry ? new Date(item.warranty_expiry) : null);
        setPlaceOfPurchase(item.place_of_purchase || '');
        setDynAttributes((item as any).attributes || {});
        setSelectedRoom(item.room);
        if (item.subcategory) {
          setSelectedSubcategory({ id: item.subcategory.id, name: item.subcategory.name });
          if (item.subcategory.category) {
            setSelectedCategory({ id: item.subcategory.category.id, name: item.subcategory.category.name });
            try {
              const { data: subs } = await supabase
                .from('item_subcategories')
                .select('id, name')
                .eq('category_id', item.subcategory.category.id)
                .order('name');
              setSubcategories(subs || []);
            } catch (e) {
              // ignore
            }
          }
        }

        // Handle receipt linkage
        if (item.receipt_id) {
          setCurrentReceiptId(item.receipt_id);
          const { data: r, error: rErr } = await supabase
            .from('receipts')
            .select('id, file_path, place_of_purchase, purchase_date')
            .eq('id', item.receipt_id)
            .single();
          if (!rErr) setSelectedReceipt(r as Receipt);
        }

        // Handle warranty document presence
        const warrantyDoc = item.item_documents?.find((d: any) => d.type === 'warranty');
        if (warrantyDoc) {
          setWarrantyFile({
            uri: '',
            name: 'Warranty',
            type: 'application/pdf',
            file_path: warrantyDoc.file_path,
          } as any);
        }

        // Load existing additional documents (non-warranty)
        const extras = (item.item_documents || []).filter((d: any) => d.type !== 'warranty').map((d: any) => ({ id: d.id, name: d.name || null, file_path: d.file_path }));
        setExistingAdditionalDocs(extras);

        // Sort images by order and generate signed URLs
        const sortedImages = [...(item.item_images || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
        
        // Store original images for later deletion comparison
        setOriginalImages(sortedImages);
        
        // Generate signed URLs for each image
        const processedImages = await Promise.all(
          sortedImages.map(async (img) => {
            const { data: signedUrlData } = await supabase.storage
              .from('item-images')
              .createSignedUrl(img.url, 3600);

            return {
              id: img.id,
              uri: signedUrlData?.signedUrl || '',
              url: img.url,
              order: img.order || 0
            };
          })
        );

        setPictures(processedImages);
      }

    } catch (error) {
      console.error('Error fetching item:', error);
      Alert.alert('Error', 'Failed to load item');
    } finally {
      setLoading(false);
    }
  }, [itemId, supabase]);

  useEffect(() => {
    fetchItem();
  }, [fetchItem]);

  useEffect(() => {
    const fetchRooms = async () => {
      if (!user) return;
      try {
        const { data, error } = await supabase
          .from('rooms')
          .select('id, name')
          .eq('user_id', user.id)
          .order('name');

        if (error) throw error;
        setRooms(data || []);
      } catch (error) {
        console.error('Error fetching rooms:', error);
        Alert.alert('Error', 'Failed to load rooms');
      } finally {
        setLoadingRooms(false);
      }
    };

    fetchRooms();
    const fetchCategories = async () => {
      try {
        const { data } = await supabase
          .from('item_categories')
          .select('id, name')
          .order('name');
        setCategories(data || []);
      } catch (e) {
        // ignore
      }
    };
    fetchCategories();
  }, [user]);

  // Load attribute definitions when subcategory changes
  useEffect(() => {
    const loadDefs = async () => {
      if (!selectedSubcategory) {
        setAttributeDefs([]);
        return;
      }
      try {
        const { data, error } = await supabase
          .from('attribute_definitions')
          .select('id,key,label,data_type,required,options,ui_control,help_text,display_order')
          .eq('subcategory_id', selectedSubcategory.id)
          .order('display_order');
        if (error) throw error;
        const defs = (data || []) as AttributeDefinition[];
        setAttributeDefs(defs);
        // Seed common fields if not already in attributes
        const next = { ...dynAttributes };
        defs.forEach((d) => {
          if (d.key === 'make' && next.make == null) next.make = make || null;
          if (d.key === 'model' && next.model == null) next.model = model || null;
          if (d.key === 'serial' && next.serial == null) next.serial = serialNumber || null;
        });
        setDynAttributes(next);
      } catch (e) {
        console.error('Error loading attribute definitions:', e);
        setAttributeDefs([]);
      }
    };
    loadDefs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubcategory]);

  // Automatically clear the image limit error when picture count is at or below the limit.
  useEffect(() => {
    if (showImageLimitError && pictures.length <= 3) {
      setShowImageLimitError(false);
    }
  }, [showImageLimitError, pictures.length]);

  const handleSave = async () => {
    if (!selectedRoom) {
      setShowRoomRequiredModal(true);
      scrollToRef(roomFieldRef);
      return;
    }

    if (!name.trim()) {
      setShowNameRequiredModal(true);
      scrollToRef(nameFieldRef);
      return;
    }
    if (!selectedCategory || !selectedSubcategory) {
      setShowCategoryRequiredError(!selectedCategory);
      setShowSubcategoryRequiredError(!selectedSubcategory);
      // Scroll to the first missing field
      if (!selectedCategory) {
        scrollToRef(categoryFieldRef);
      } else if (!selectedSubcategory) {
        scrollToRef(subcategoryFieldRef);
      }
      return;
    }

    if (pictures.length > 3) {
      setShowImageLimitError(true);
      scrollToRef(imageFieldRef);
      return;
    }

    if (price && !validatePrice(price)) {
      scrollToRef(priceFieldRef);
      return;
    }

    setLoading(true);

    try {
      // Handle receipt logic before saving item
      let receiptId: string | null = selectedReceipt ? selectedReceipt.id : currentReceiptId;
      let receiptFilePath: string | null = null;

      if (!receiptId && receiptFile) {
        // upload new file
        const fileExt = receiptFile.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${user?.id}/${fileName}`;
        const resp = await fetch(receiptFile.uri);
        const blob = await resp.blob();
        const { error: uploadError } = await supabase.storage
          .from('item-documents')
          .upload(filePath, blob, { upsert: true });
        if (uploadError) throw uploadError;
        receiptFilePath = filePath;

        // create receipt row
        const { data: newRec, error: recErr } = await supabase
          .from('receipts')
          .insert({
            user_id: user?.id,
            file_path: filePath,
            place_of_purchase: placeOfPurchase || null,
            purchase_date: purchaseDate ? purchaseDate.toISOString() : null,
          })
          .select()
          .single();
        if (recErr) throw recErr;
        receiptId = newRec.id;
      }

      // Handle warranty file
      let warrantyFilePath: string | null = null;
      if (warrantyFile) {
        const ext = resolveFileExtension(warrantyFile.name, (warrantyFile as any).type ?? null);
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${ext}`;
        const filePath = `${user?.id}/${fileName}`;
        const respW = await fetch(warrantyFile.uri);
        const blobW = await respW.blob();
        const safeContentType = ((warrantyFile as any).type && !(warrantyFile as any).type.includes('*'))
          ? (warrantyFile as any).type
          : (blobW.type || (ext === 'pdf' ? 'application/pdf' : 'application/octet-stream'));
        const { error: warrantyUploadErr } = await supabase.storage
          .from('item-documents')
          .upload(filePath, blobW, { upsert: true, contentType: safeContentType });
        if (warrantyUploadErr) throw warrantyUploadErr;
        warrantyFilePath = filePath;
      }

      // Save item details
      const { data: updatedItem, error: updateError } = await supabase
        .from('items')
        .update({
          name,
          description: description || null,
          attributes: {
            ...dynAttributes,
            ...(make ? { make } : {}),
            ...(model ? { model } : {}),
            ...(serialNumber ? { serial: serialNumber } : {}),
          },
          purchase_date: purchaseDate ? purchaseDate.toISOString() : null,
          price: price ? parseFloat(price) : null,
          warranty_expiry: warrantyExpiry ? warrantyExpiry.toISOString() : null,
          place_of_purchase: placeOfPurchase || null,
          room_id: selectedRoom.id,
          subcategory_id: selectedSubcategory.id,
          receipt_id: receiptId,
        })
        .eq('id', itemId)
        .select()
        .single();

      if (updateError) throw updateError;

      // Upsert warranty document if provided
      if (warrantyFilePath) {
        // Remove any existing warranty doc for this item to avoid duplicates
        await supabase
          .from('item_documents')
          .delete()
          .eq('item_id', itemId)
          .eq('type', 'warranty');

        const { error: docErr } = await supabase.from('item_documents').insert({
          item_id: itemId,
          user_id: user?.id,
          type: 'warranty',
          file_path: warrantyFilePath,
        });
        if (docErr) throw docErr;
      }

      // Insert any newly added additional documents
      if (pendingAdditionalDocs.length > 0) {
        for (const doc of pendingAdditionalDocs) {
          const ext = resolveFileExtension(doc.name, doc.type);
          const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${ext}`;
          if (!user?.id) {
            console.error('Upload aborted: missing user.id for storage path');
            throw new Error('Not authenticated');
          }
          const storagePath = `${user.id}/${itemId}/${fileName}`;
          const resp = await fetch(doc.uri);
          const blob = await resp.blob();
          const safeContentType = doc.type && !doc.type.includes('*') ? doc.type : (blob.type || 'application/octet-stream');
          console.log('Uploading additional doc to path:', storagePath, 'contentType:', safeContentType);
          let uploadErr = null as any;
          try {
            const { error } = await supabase.storage
              .from('item-documents')
              .upload(storagePath, blob, { upsert: true, contentType: safeContentType });
            uploadErr = error;
          } catch (e) {
            uploadErr = e;
          }
          if (uploadErr) {
            console.error('Additional doc upload failed:', uploadErr);
            throw uploadErr;
          }
          const { error: insErr } = await supabase.from('item_documents').insert({
            item_id: itemId,
            user_id: user?.id,
            type: 'other',
            name: doc.name,
            file_path: storagePath,
            mime_type: safeContentType,
          });
          if (insErr) throw insErr;
        }
      }

      // Process images
      const processedImages = await Promise.all(
        pictures
          .filter(p => !p.isDeleted)
          .map(async (picture, index) => {
            if (picture.uri && !picture.url) {
              // For new images, upload them
              const filePath = `${user?.id}/${itemId}/${picture.id}.jpg`;
              const response = await fetch(picture.uri);
              const blob = await response.blob();
              
              const { error: uploadError } = await supabase.storage
                .from('item-images')
                .upload(filePath, blob, {
                  contentType: 'image/jpeg'
                });

              if (uploadError) throw uploadError;

              // Store the storage path (not public URL) in DB just like when creating items
              return {
                id: picture.id, // temporary id; may not be UUID
                url: filePath,
                order: index,
              };
            }

            // For existing images, just return their current data
            return {
              id: picture.id,
              url: picture.url || '',
              order: picture.order,
            };
          })
      );

      console.log('💾 [EditItemScreen] Processed images for saving:', processedImages.map(img => ({
        id: img.id,
        order: img.order,
        url: img.url
      })));

      // Split into existing vs new images to avoid null id errors
      const existingImagesForUpsert = processedImages.filter(img => !img.id.startsWith('new_')).map((img: any) => ({
        id: img.id,
        item_id: itemId,
        url: img.url,
        order: img.order,
      }));

      const newImagesForInsert = processedImages.filter(img => img.id.startsWith('new_')).map((img: any) => ({
        item_id: itemId,
        url: img.url,
        order: img.order,
      }));

      if (existingImagesForUpsert.length > 0) {
        console.log('💾 [EditItemScreen] Upserting existing images:', existingImagesForUpsert);
        const { error: upsertErr } = await supabase
          .from('item_images')
          .upsert(existingImagesForUpsert);
        if (upsertErr) {
          console.error('❌ [EditItemScreen] Error upserting existing images:', upsertErr);
          throw upsertErr;
        }
      }

      if (newImagesForInsert.length > 0) {
        console.log('💾 [EditItemScreen] Inserting new images:', newImagesForInsert);
        const { error: insertErr } = await supabase
          .from('item_images')
          .insert(newImagesForInsert);
        if (insertErr) {
          console.error('❌ [EditItemScreen] Error inserting new images:', insertErr);
          throw insertErr;
        }
      }

      // Delete images that the user removed
      const deletedImageIds = originalImages
        .filter(img => !processedImages.some(p => p.id === img.id))
        .map(img => img.id);

      if (deletedImageIds.length > 0) {
        const { error: deleteError } = await supabase
          .from('item_images')
          .delete()
          .in('id', deletedImageIds);

        if (deleteError) {
          console.error('❌ [EditItemScreen] Error deleting removed images:', deleteError);
          throw deleteError;
        }

        console.log('🗑️ [EditItemScreen] Deleted images from database:', deletedImageIds);
      }

      console.log('💾 [EditItemScreen] Image orders updated in database successfully');

      // Clear the image cache again after all operations are complete
      console.log('🗑️ [EditItemScreen] Clearing image cache for item:', itemId);
      await clearItemCache(itemId);

      // Wait for database to actually reflect the changes by polling
      console.log('⏳ [EditItemScreen] Waiting for database to reflect changes...');
      let attempts = 0;
      const maxAttempts = 10;
      let dataUpdated = false;
      
      while (attempts < maxAttempts && !dataUpdated) {
        attempts++;
        console.log(`🔍 [EditItemScreen] Checking database for updates (attempt ${attempts}/${maxAttempts})`);
        
        // Use the exact same query structure as the main data fetching to avoid read replica lag
        const { data: checkData, error: checkError } = await supabase
          .from('items')
          .select(`
            id,
            item_images (
              id,
              url,
              "order",
              updated_at
            )
          `)
          .eq('id', itemId)
          .eq('user_id', user?.id)
          .single();
        
        if (checkError) {
          console.error('❌ [EditItemScreen] Error checking database:', checkError);
          break;
        }
        
        // Check if the first image is the one we expect (the one that should now be first)
        if (checkData && checkData.item_images && checkData.item_images.length > 0) {
          const expectedFirstImageId = processedImages.find(img => img.order === 0)?.id;
          const actualFirstImage = checkData.item_images.sort((a, b) => a.order - b.order)[0];
          
          console.log(`📊 [EditItemScreen] Database check - Expected first image: ${expectedFirstImageId}, Actual first image: ${actualFirstImage.id}`);
          console.log(`📊 [EditItemScreen] Database check - Image order:`, checkData.item_images.map(img => ({ id: img.id, order: img.order, updated_at: img.updated_at })));
          
          if (actualFirstImage.id === expectedFirstImageId) {
            console.log('✅ [EditItemScreen] Database reflects updated order');
            dataUpdated = true;
            break;
          } else {
            console.log(`⏳ [EditItemScreen] Database still shows old order. Expected: ${expectedFirstImageId}, Got: ${actualFirstImage.id}`);
            console.log(`📊 [EditItemScreen] Current image order:`, checkData.item_images.map(img => ({ id: img.id, order: img.order })));
            // Wait 100ms before next check
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      }
      
      if (!dataUpdated) {
        console.warn('⚠️ [EditItemScreen] Database may not have fully updated, proceeding anyway');
      }

      // No need to call refresh() here; ItemList will refresh when it receives the { refresh: true } param.

      navigation.dispatch(
        CommonActions.navigate({
          name: 'ItemsTab',
          params: {
            page: returnPage,
            refresh: true,
            editedItemId: itemId
          },
        })
      );

    } catch (error) {
      console.error('Error saving item:', error);
      Alert.alert('Error', 'Failed to save item. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Delete item logic
  const confirmDeleteItem = async () => {
    try {
      // 1) Fetch the receipt_id before deleting the item
      const { data: itemRow, error: fetchErr } = await supabase
        .from('items')
        .select('receipt_id')
        .eq('id', itemId)
        .single();

      if (fetchErr) throw fetchErr;

      const receiptId = itemRow?.receipt_id as string | null;

      // 2) Delete the item row itself
      const { error: deleteErr } = await supabase.from('items').delete().eq('id', itemId);
      if (deleteErr) throw deleteErr;

      // 3) If item had a receipt, check if still referenced
      if (receiptId) {
        const { count, error: cntErr } = await supabase
          .from('items')
          .select('id', { head: true, count: 'exact' })
          .eq('receipt_id', receiptId);
        if (cntErr) throw cntErr;

        if (count === 0) {
          const { data: rec, error: recErr } = await supabase
            .from('receipts')
            .select('file_path')
            .eq('id', receiptId)
            .single();
          if (recErr) throw recErr;
          if (rec?.file_path) {
            await supabase.storage.from('item-documents').remove([rec.file_path]);
          }
          await supabase.from('receipts').delete().eq('id', receiptId);
        }
      }

      // Navigate back to Items list and trigger refresh
      navigation.dispatch(
        CommonActions.navigate({ name: 'ItemsTab', params: { refresh: true } })
      );
    } catch (err) {
      console.error('Error deleting item', err);
      Alert.alert('Error', 'Failed to delete item');
    }
  };

  // Modal rendering functions (copied and adapted from AddItemScreen)
  const renderRoomModal = () => (
    <Modal
      visible={showRoomModal}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowRoomModal(false)}
    >
      <View style={[styles.modalContainer, { backgroundColor: 'rgba(0,0,0,0.4)' }]}> 
        <View style={[styles.modalContent, { backgroundColor: theme.card, padding: 20 }]}> 
          <View style={styles.modalHeader}> 
            <Text style={[styles.modalTitle, { color: theme.text }]}>Select Room</Text> 
            <TouchableOpacity
              onPress={() => setShowRoomModal(false)}
              style={[styles.closeButton, { backgroundColor: theme.inputBackground, borderRadius: 16, borderWidth: 1, borderColor: theme.border }]}
            >
              <Ionicons name="close" size={24} color={theme.text} />
            </TouchableOpacity>
          </View>
          <ScrollView>
            {rooms.map((room) => (
              <TouchableOpacity
                key={room.id}
                style={[
                  styles.roomOption,
                  selectedRoom?.id === room.id && styles.selectedRoom,
                  { borderBottomWidth: 1, borderBottomColor: theme.border }
                ]}
                onPress={() => {
                  setSelectedRoom(room);
                  setShowRoomModal(false);
                }}
              >
                <Text style={[styles.roomOptionText, selectedRoom?.id === room.id && { color: theme.accent }, selectedRoom?.id !== room.id && { color: theme.text }]}> 
                  {room.name.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  // Resolve a safe file extension from provided name or MIME type.
  const resolveFileExtension = (fileName?: string | null, mime?: string | null) => {
    const nameExt = (fileName || '').split('.').pop()?.toLowerCase();
    if (nameExt && /^[a-z0-9]{1,8}$/.test(nameExt)) return nameExt;
    const mimeToExt: Record<string, string> = {
      'application/pdf': 'pdf',
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/png': 'png',
      'image/webp': 'webp',
      'image/heic': 'heic',
      'image/heif': 'heif',
    };
    if (mime && mimeToExt[mime]) return mimeToExt[mime];
    if (mime && mime.startsWith('image/')) return mime.split('/')[1];
    return 'bin';
  };

  const renderCategoryModal = () => (
    <Modal
      visible={showCategoryModal}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowCategoryModal(false)}
    >
      <View style={[styles.modalContainer, { backgroundColor: 'rgba(0,0,0,0.4)' }]}> 
        <View style={[styles.modalContent, { backgroundColor: theme.card, padding: 20 }]}> 
          <View style={styles.modalHeader}> 
            <Text style={[styles.modalTitle, { color: theme.text }]}>Select Category</Text> 
            <TouchableOpacity
              onPress={() => setShowCategoryModal(false)}
              style={[styles.closeButton, { backgroundColor: theme.inputBackground, borderRadius: 16, borderWidth: 1, borderColor: theme.border }]}
            >
              <Ionicons name="close" size={24} color={theme.text} />
            </TouchableOpacity>
          </View>
          <ScrollView>
            {categories.map((cat) => (
              <TouchableOpacity
                key={cat.id}
                style={[
                  styles.roomOption,
                  selectedCategory?.id === cat.id && styles.selectedRoom,
                  { borderBottomWidth: 1, borderBottomColor: theme.border }
                ]}
                onPress={() => {
                  setSelectedCategory(cat);
                  setShowCategoryRequiredError(false);
                  setSelectedSubcategory(null);
                  setShowSubcategoryRequiredError(false);
                  supabase
                    .from('item_subcategories')
                    .select('id, name')
                    .eq('category_id', cat.id)
                    .order('name')
                    .then(({ data }) => setSubcategories(data || []));
                  setShowCategoryModal(false);
                }}
              >
                <Text style={[styles.roomOptionText, selectedCategory?.id === cat.id && { color: theme.accent }, selectedCategory?.id !== cat.id && { color: theme.text }]}>
                  {cat.name.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
          <View style={{ height: 1, backgroundColor: theme.border, marginVertical: 12 }} />
          <ThemedButton
            title="Suggest new category"
            variant="secondary"
            onPress={() => { setShowCategoryModal(false); setShowSuggestCategoryModal(true); }}
            style={{ alignSelf: 'flex-start' }}
          />
        </View>
      </View>
    </Modal>
  );

  const renderSubcategoryModal = () => (
    <Modal
      visible={showSubcategoryModal}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowSubcategoryModal(false)}
    >
      <View style={[styles.modalContainer, { backgroundColor: 'rgba(0,0,0,0.4)' }]}> 
        <View style={[styles.modalContent, { backgroundColor: theme.card, padding: 20 }]}> 
          <View style={styles.modalHeader}> 
            <Text style={[styles.modalTitle, { color: theme.text }]}>Select Subcategory</Text> 
            <TouchableOpacity
              onPress={() => setShowSubcategoryModal(false)}
              style={[styles.closeButton, { backgroundColor: theme.inputBackground, borderRadius: 16, borderWidth: 1, borderColor: theme.border }]}
            >
              <Ionicons name="close" size={24} color={theme.text} />
            </TouchableOpacity>
          </View>
          <ScrollView>
            {subcategories.map((sub) => (
              <TouchableOpacity
                key={sub.id}
                style={[
                  styles.roomOption,
                  selectedSubcategory?.id === sub.id && styles.selectedRoom,
                  { borderBottomWidth: 1, borderBottomColor: theme.border }
                ]}
                onPress={() => {
                  setSelectedSubcategory(sub);
                  setShowSubcategoryRequiredError(false);
                  setShowSubcategoryModal(false);
                }}
              >
                <Text style={[styles.roomOptionText, selectedSubcategory?.id === sub.id && { color: theme.accent }, selectedSubcategory?.id !== sub.id && { color: theme.text }]}>
                  {sub.name.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
          <View style={{ height: 1, backgroundColor: theme.border, marginVertical: 12 }} />
          <ThemedButton
            title="Suggest new subcategory"
            variant="secondary"
            onPress={() => { setShowSubcategoryModal(false); setShowSuggestSubcategoryModal(true); }}
            style={{ alignSelf: 'flex-start' }}
          />
        </View>
      </View>
    </Modal>
  );

  const submitCategorySuggestion = async () => {
    if (!user) return;
    if (!proposedCategoryName.trim()) {
      Alert.alert('Category name required');
      return;
    }
    try {
      setSuggestLoading(true);
      const { error } = await supabase.from('taxonomy_requests').insert({
        user_id: user.id,
        kind: 'category',
        proposed_category_name: proposedCategoryName.trim(),
        status: 'new'
      });
      if (error) throw error;
      setProposedCategoryName('');
      setShowSuggestCategoryModal(false);
      Alert.alert('Suggestion submitted', 'Thank you for your suggestion.');
    } catch (e) {
      Alert.alert('Error', 'Failed to submit suggestion.');
    } finally {
      setSuggestLoading(false);
    }
  };

  const submitSubcategorySuggestion = async () => {
    if (!user || !selectedCategory) return;
    if (!proposedSubcategoryName.trim()) {
      Alert.alert('Subcategory name required');
      return;
    }
    try {
      setSuggestLoading(true);
      const { error } = await supabase.from('taxonomy_requests').insert({
        user_id: user.id,
        kind: 'subcategory',
        proposed_subcategory_name: proposedSubcategoryName.trim(),
        parent_category_id: selectedCategory.id,
        status: 'new'
      });
      if (error) throw error;
      setProposedSubcategoryName('');
      setShowSuggestSubcategoryModal(false);
      Alert.alert('Suggestion submitted', 'Thank you for your suggestion.');
    } catch (e) {
      Alert.alert('Error', 'Failed to submit suggestion.');
    } finally {
      setSuggestLoading(false);
    }
  };

  const renderRoomRequiredModal = () => (
    <Modal
      visible={showRoomRequiredModal}
      transparent
      animationType="fade"
      onRequestClose={() => setShowRoomRequiredModal(false)}
    >
      <View style={[styles.modalOverlay, { backgroundColor: theme.background }]}> 
        <View style={[styles.deleteModalContent, { backgroundColor: theme.card }]}> 
          <Text style={styles.deleteModalTitle}>Room Required</Text> 
          <Text style={styles.deleteModalText}>Please select a room before editing an item.</Text> 
          <View style={styles.deleteModalButtons}> 
            <TouchableOpacity
              style={[styles.deleteModalButton, styles.cancelButton]}
              onPress={() => setShowRoomRequiredModal(false)}
            >
              <Text style={styles.cancelButtonText}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  const renderNameRequiredModal = () => (
    <Modal
      visible={showNameRequiredModal}
      transparent
      animationType="fade"
      onRequestClose={() => setShowNameRequiredModal(false)}
    >
      <View style={[styles.modalOverlay, { backgroundColor: theme.background }]}> 
        <View style={[styles.deleteModalContent, { backgroundColor: theme.card }]}> 
          <Text style={styles.deleteModalTitle}>Name Required</Text> 
          <Text style={styles.deleteModalText}>Please enter a name for your item.</Text> 
          <View style={styles.deleteModalButtons}> 
            <TouchableOpacity
              style={[styles.deleteModalButton, styles.cancelButton]}
              onPress={() => setShowNameRequiredModal(false)}
            >
              <Text style={styles.cancelButtonText}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // Helper to scroll to a ref
  const scrollToRef = (ref: any) => {
    if (!ref?.current || !scrollViewRef.current) return;
    ref.current.measureLayout(
      scrollViewRef.current.getInnerViewNode(),
      (x: number, y: number) => {
        const offset = headerHeight + 24;
        scrollViewRef.current?.scrollTo({ y: Math.max(y - offset, 0), animated: true });
      }
    );
  };

  const getOriginalUrlFromUri = (uri: string, itemImages: { url: string }[]) => {
    // Try to find the original url for a given signed uri
    if (!uri || !itemImages) return null;
    // The signed URL contains the original path as a substring
    const found = itemImages.find(img => uri.includes(img.url));
    return found ? found.url : null;
  };

  // Add validatePrice function
  const validatePrice = (value: string) => {
    if (!value) {
      setPriceError('');
      return true;
    }
    const regex = /^\d*\.?\d{0,2}$/;
    if (!regex.test(value)) {
      setPriceError('Please enter a valid price (e.g., 10.99)');
      return false;
    }
    const numValue = parseFloat(value);
    if (numValue > 999999999.99) {
      setPriceError('Price seems too high');
      return false;
    }
    setPriceError('');
    return true;
  };

  // Add validatePurchaseDate function
  const validatePurchaseDate = (date: Date) => {
    if (!date) return true;
    return date <= new Date();
  };

  // Add renderDatePicker function
  const renderDatePicker = () => {
    if (!showDatePicker && !showWarrantyPicker) return null;
    if (Platform.OS === 'web') {
      return (
        <Modal
          key={dateFormat}
          visible={showDatePicker || showWarrantyPicker}
          transparent={true}
          animationType="fade"
          onRequestClose={() => {
            setShowDatePicker(false);
            setShowWarrantyPicker(false);
          }}
          accessibilityViewIsModal
          statusBarTranslucent
        >
          <View style={{ backgroundColor: 'rgba(0,0,0,0.4)', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
            <View style={{ backgroundColor: theme.card, borderRadius: 20, padding: 24, maxWidth: 400, width: '90%', alignSelf: 'center', alignItems: 'center', justifyContent: 'center', position: 'relative' }} onTouchEnd={e => e.stopPropagation()}>
              <Text style={[styles.modalTitle, { color: theme.text, textAlign: 'center', marginBottom: 24, width: '100%' }]}> 
                {datePickerMode === 'purchase' ? 'Select Purchase Date' : 'Select Warranty Expiry'}
              </Text>
              <TouchableOpacity
                onPress={() => {
                  setShowDatePicker(false);
                  setShowWarrantyPicker(false);
                }}
                style={{ position: 'absolute', top: 16, right: 16, backgroundColor: theme.inputBackground, borderRadius: 16, borderWidth: 1, borderColor: theme.border, padding: 4, zIndex: 2 }}
                accessibilityRole="button"
                accessibilityLabel="Close date picker"
                accessibilityHint="Close the date picker without selecting a date"
              >
                <Ionicons name="close" size={24} color={theme.text} />
              </TouchableOpacity>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', width: '100%', marginBottom: 28 }}>
                <input
                  type="date"
                  value={tempDate.toISOString().split('T')[0]}
                  onChange={(e) => {
                    const value = e.target.value;
                    const newDate = new Date(value);
                    if (!isNaN(newDate.getTime())) {
                      setTempDate(newDate);
                      if (datePickerMode === 'purchase') {
                        validatePurchaseDate(newDate);
                      }
                      if (datePickerMode === 'warranty') {
                        setWarrantyExpiry(newDate);
                      }
                    }
                  }}
                  style={{
                    backgroundColor: theme.inputBackground,
                    color: theme.text,
                    border: `1px solid ${theme.border}`,
                    borderRadius: 8,
                    padding: 12,
                    fontSize: 16,
                    width: '100%',
                    maxWidth: '300px',
                    marginRight: 8,
                  }}
                  aria-label={datePickerMode === 'purchase' ? 'Purchase date' : 'Warranty expiry date'}
                />
                {/* Removed redundant calendar icon */}
              </div>
              {datePickerMode === 'purchase' && purchaseDateError ? (
                <Text style={{ color: theme.error || '#d32f2f', fontWeight: '600', fontSize: 14, marginBottom: 12, textAlign: 'center' }}>
                  {purchaseDateError}
                </Text>
              ) : null}
              <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 16, width: '100%' }}>
                <ThemedButton
                  title="Cancel"
                  variant="secondary"
                  onPress={() => {
                    setShowDatePicker(false);
                    setShowWarrantyPicker(false);
                  }}
                  style={{ flex: 1 }}
                />
                <ThemedButton
                  title="Confirm"
                  variant="primary"
                  onPress={() => {
                    if (datePickerMode === 'purchase') {
                      if (validatePurchaseDate(tempDate)) {
                        setPurchaseDate(tempDate);
                        setShowDatePicker(false);
                      }
                    } else {
                      setWarrantyExpiry(tempDate);
                      setShowWarrantyPicker(false);
                    }
                  }}
                  style={{ flex: 1 }}
                />
              </View>
            </View>
          </View>
        </Modal>
      );
    }
    // For native platforms, use the native date picker
    const DateTimePicker = require('@react-native-community/datetimepicker').default;
    return (
      <DateTimePicker
        value={datePickerMode === 'purchase' ? (purchaseDate || new Date()) : (warrantyExpiry || new Date())}
        mode="date"
        display="default"
        onChange={(event: any, selectedDate?: Date) => {
          if (event.type === 'set' && selectedDate) {
            if (datePickerMode === 'purchase') {
              if (validatePurchaseDate(selectedDate)) {
                setPurchaseDate(selectedDate);
                setShowDatePicker(false);
              }
            } else {
              setWarrantyExpiry(selectedDate);
              setShowWarrantyPicker(false);
            }
          } else {
            setShowDatePicker(false);
            setShowWarrantyPicker(false);
          }
        }}
      />
    );
  };

  // Helper functions now inside the component
  const formatDate = (date: Date | null) => {
    if (!date) return '';
    switch (dateFormat) {
      case 'DD/MM/YYYY':
        return date.toLocaleDateString('en-GB');
      case 'MM/DD/YYYY':
        return date.toLocaleDateString('en-US');
      case 'YYYY-MM-DD':
        return date.toISOString().split('T')[0];
      default:
        return date.toLocaleDateString('en-GB');
    }
  };

  const handlePriceChange = (value: string) => {
    const cleaned = value.replace(/[^0-9.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length > 2) return;
    if (parts[1] && parts[1].length > 2) return;
    setPrice(cleaned);
    validatePrice(cleaned);
  };

  const handleDragEnd = useCallback((newOrder: Picture[]) => {
    const reindexedPictures = reindexPictures(newOrder);
    setPictures(reindexedPictures);
  }, []);

  const handlePickItemPictures = useCallback(async () => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsMultipleSelection: true,
        quality: 0.8,
      });

      if (!result.canceled && result.assets.length > 0) {
        const newPictures = result.assets.map(asset => ({
          id: `new_${Date.now()}_${Math.random().toString(36).substring(7)}`,
          uri: asset.uri,
          order: pictures.filter(p => !p.isDeleted).length + result.assets.indexOf(asset)
        }));
        
        // Filter out any invalid pictures
        const validPictures = newPictures.filter(pic => pic.uri);
        
        if (validPictures.length > 0) {
          // Filter out deleted pictures before adding new ones
          const activePictures = pictures.filter(p => !p.isDeleted);
          const updatedPictures = [...activePictures, ...validPictures];
          
          const reindexed = reindexPictures(updatedPictures);
          setPictures(reindexed);
        }
      }
    } catch (error) {
      console.error('Error picking images:', error);
      Alert.alert('Error', 'Failed to pick images');
    }
  }, [pictures, reindexPictures]);

  const startScanning = async () => {
    Alert.alert('Barcode scanning not implemented in this snippet.');
  };

  const handlePickDocument = async (type: 'receipt' | 'warranty') => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['application/pdf', 'image/*'],
        copyToCacheDirectory: true,
      });

      if (result.canceled) return;

      const file: FileWithPath = {
        uri: result.assets[0].uri,
        name: result.assets[0].name || 'document',
        type: result.assets[0].mimeType || 'application/pdf',
        file_path: result.assets[0].uri
      };

      if (type === 'receipt') {
        setReceiptFile(file);
      } else {
        setWarrantyFile(file);
      }
    } catch (error) {
      console.error('Error picking document:', error);
      Alert.alert('Error', 'Failed to pick document');
    }
  };

  const handlePickReceipt = () => handlePickDocument('receipt');
  const handlePickWarranty = () => handlePickDocument('warranty');

  const handlePickAdditionalDocument = async () => {
    try {
      const totalCount = existingAdditionalDocs.length + pendingAdditionalDocs.length;
      if (totalCount >= MAX_ADDITIONAL_DOCS) {
        Alert.alert('Limit reached', `You can upload up to ${MAX_ADDITIONAL_DOCS} additional documents.`);
        return;
      }
      const result = await DocumentPicker.getDocumentAsync({
        type: ['application/pdf', 'image/*'],
        copyToCacheDirectory: true,
      });
      if (result.canceled) return;
      const file = {
        uri: result.assets[0].uri,
        name: result.assets[0].name || 'document',
        type: result.assets[0].mimeType || 'application/octet-stream',
      };
      setPendingAdditionalDocFile(file as any);
      setPendingAdditionalDocName('');
      setShowAddDocNameModal(true);
    } catch (e) {
      console.error('Error picking additional document:', e);
      Alert.alert('Error', 'Failed to pick document');
    }
  };

  const confirmAddAdditionalDocument = () => {
    if (!pendingAdditionalDocFile) return;
    const trimmed = pendingAdditionalDocName.trim();
    if (!trimmed) {
      Alert.alert('Name required', 'Please enter a document name.');
      return;
    }
    setPendingAdditionalDocs((prev) => [...prev, { uri: pendingAdditionalDocFile.uri, name: trimmed, type: pendingAdditionalDocFile.type }]);
    setPendingAdditionalDocFile(null);
    setPendingAdditionalDocName('');
    setShowAddDocNameModal(false);
  };

  const removeExistingAdditionalDocument = async (docId: string, filePath: string) => {
    try {
      await supabase.storage.from('item-documents').remove([filePath]);
      await supabase.from('item_documents').delete().eq('id', docId);
      setExistingAdditionalDocs((prev) => prev.filter((d) => d.id !== docId));
    } catch (e) {
      console.error('Error deleting document:', e);
      Alert.alert('Error', 'Failed to delete document');
    }
  };

  const removePendingAdditionalDocumentAt = (index: number) => {
    setPendingAdditionalDocs((prev) => prev.filter((_, i) => i !== index));
  };

  const submitRenameAdditionalDocument = async () => {
    const id = renameDocModal.id;
    const trimmed = (renameDocModal.name || '').trim();
    if (!id) {
      setRenameDocModal({ id: null, name: '' });
      return;
    }
    if (!trimmed) {
      Alert.alert('Name required', 'Please enter a document name.');
      return;
    }
    try {
      const { error } = await supabase.from('item_documents').update({ name: trimmed }).eq('id', id);
      if (error) throw error;
      setExistingAdditionalDocs(prev => prev.map(d => d.id === id ? { ...d, name: trimmed } : d));
      setRenameDocModal({ id: null, name: '' });
    } catch (e) {
      console.error('Error renaming document:', e);
      Alert.alert('Error', 'Failed to rename document');
    }
  };

  const handlePickImage = async (type: 'receipt' | 'warranty') => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [4, 3],
        quality: 0.8,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        const asset = result.assets[0];
        if (asset.uri) {
          const file: FileWithPath = {
            uri: asset.uri,
            name: asset.uri.split('/').pop() || 'image',
            type: 'image/jpeg',
            file_path: asset.uri
          };

          if (type === 'receipt') {
            setReceiptFile(file);
          } else {
            setWarrantyFile(file);
          }
        }
      }
    } catch (error) {
      console.error('Error picking image:', error);
      Alert.alert('Error', 'Failed to pick image');
    }
  };

  // Previously inline components defined inside caused remount on every render.
  // Use stable, top-level components declared above instead.

  // Memoize the WebImageGrid props to prevent unnecessary re-renders
  const imageGridProps = useMemo(() => ({
    pictures,
    onReorder: handleDragEnd,
    onDelete: (id: string) => {
      const index = pictures.findIndex(p => p.id === id);
      if (index !== -1) {
        handleDeletePicture(index);
      }
    },
    onAdd: handlePickItemPictures
  }), [pictures, handleDragEnd, handleDeletePicture, handlePickItemPictures]);

  // Stable handlers for Web grid to avoid unnecessary re-renders when unrelated state changes
  const webOnDeletePicture = useCallback((id: string) => {
    const index = pictures.findIndex(p => p.id === id);
    if (index !== -1) {
      handleDeletePicture(index);
    }
  }, [pictures, handleDeletePicture]);

  const webOnAddPicture = useCallback(() => {
    handlePickItemPictures();
  }, [handlePickItemPictures]);

  // Helper to unlink and possibly delete a receipt
  const removeLinkedReceipt = async () => {
    if (!currentReceiptId) return;
    // Unlink this item
    await supabase.from('items').update({ receipt_id: null }).eq('id', itemId);

    // Check if any other items still reference this receipt
    const { count, error: cntErr } = await supabase
      .from('items')
      .select('id', { count: 'exact', head: true })
      .eq('receipt_id', currentReceiptId);
    if (cntErr) {
      console.error('Error counting receipt usage', cntErr);
    }

    if (!cntErr && count === 0) {
      // safe to delete file & receipt row
      const { data: rec } = await supabase
        .from('receipts')
        .select('file_path')
        .eq('id', currentReceiptId)
        .single();
      if (rec && rec.file_path) {
        await supabase.storage.from('item-documents').remove([rec.file_path]);
      }
      await supabase.from('receipts').delete().eq('id', currentReceiptId);
    }

    setCurrentReceiptId(null);
    setSelectedReceipt(null);
  };

  // Show advisory before receipt upload if necessary
  const handleAddReceiptPress = () => {
    const missingPlace = !placeOfPurchase.trim();
    const missingDate = !purchaseDate;
    if (missingPlace || missingDate) {
      let msg = 'It is recommended to add ';
      if (missingPlace && missingDate) msg += 'a place of purchase and a date of purchase';
      else if (missingPlace) msg += 'a place of purchase';
      else msg += 'a date of purchase';
      msg += ' before uploading your receipt';
      setReceiptAdviceMessage(msg);
      setShowReceiptAdviceModal(true);
    } else {
      setShowReceiptModal(true);
    }
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}> 
      <StatusBar barStyle="light-content" />
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={[styles.container, { backgroundColor: theme.background }]}
      >
        <ScrollView
          ref={scrollViewRef}
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          stickyHeaderIndices={[0]}
        >
          {/* Modern Header */}
          <View
            onLayout={({ nativeEvent }) => setHeaderHeight(nativeEvent.layout.height)}
            style={{ paddingTop: 32, paddingBottom: 16, paddingHorizontal: 20, alignItems: 'center', backgroundColor: theme.background }}>
            <View style={{ position: 'absolute', left: 20, top: 32 }}>
              <TouchableOpacity
                style={{ padding: 8, borderRadius: 20, backgroundColor: theme.inputBackground }}
                onPress={() => navigation.goBack()}
                accessibilityLabel="Back"
                accessibilityRole="button"
              >
                <Ionicons name="arrow-back" size={26} color={theme.text} />
              </TouchableOpacity>
            </View>
            <Text style={{ color: theme.text, fontSize: 17, fontWeight: '500', textAlign: 'center', marginBottom: 4 }}>Edit Item</Text>
            <View style={{ height: 2, backgroundColor: theme.border, width: 60, borderRadius: 2, marginTop: 8 }} />
          </View>
          {/* Modern Card Form */}
          <View style={{ padding: 20, alignItems: 'center' }}>
            <View style={{ backgroundColor: theme.card, borderRadius: 16, padding: 24, width: '100%', maxWidth: 600, ...Platform.select({
              ios: {
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.25,
                shadowRadius: 3.84,
              },
              android: {
                elevation: 3,
              },
              web: {
                boxShadow: '0 2px 8px 0 rgba(0,0,0,0.08)',
              },
            }), ...glowStyle }}>
              {/* General Info Section */}
              <Text style={{ color: theme.text, fontSize: 17, fontWeight: '500', marginBottom: 16, marginTop: 8 }}>General Info</Text>
              <View style={{ gap: 12 }}>
                <FormLabel>Name *</FormLabel>
                <View>
                  <BaseTextInput
                    ref={nameFieldRef}
                    style={{
                      backgroundColor: theme.inputBackground,
                      color: theme.text,
                      borderColor: showNameRequiredModal ? (theme.error || '#d32f2f') : theme.border,
                      borderWidth: 1,
                      borderRadius: 8,
                      paddingVertical: 12,
                      paddingHorizontal: 14,
                      fontSize: 16,
                    }}
                    value={name}
                    onChangeText={(text) => {
                      setName(text);
                      if (showNameRequiredModal) setShowNameRequiredModal(false);
                    }}
                    placeholder="Enter item name"
                    placeholderTextColor={theme.secondaryText}
                    autoCapitalize="words"
                    maxLength={25}
                  />
                  {showNameRequiredModal && (
                    <Text style={{ color: theme.error || '#d32f2f', fontWeight: '500', fontSize: 14, marginTop: 4, marginBottom: 2 }}>Item name is required</Text>
                  )}
                  <Text style={{ color: theme.secondaryText, fontSize: 12, textAlign: 'right', marginTop: 2 }}>{name.length}/25 characters</Text>
                </View>
                {/* Room moved directly below Name */}
                <FormLabel>Room *</FormLabel>
                <TouchableOpacity
                  ref={roomFieldRef}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: theme.inputBackground,
                    borderColor: showRoomRequiredModal ? (theme.error || '#d32f2f') : theme.border,
                    borderWidth: 1,
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 0,
                    height: 44,
                  }}
                  onPress={() => {
                    setShowRoomModal(true);
                    if (showRoomRequiredModal) {
                      setShowRoomRequiredModal(false);
                    }
                  }}
                >
                  <Text style={{ color: selectedRoom ? theme.text : theme.secondaryText, fontSize: 16 }}>
                    {selectedRoom ? selectedRoom.name.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()) : 'Select a room'}
                  </Text>
                  <Ionicons name="chevron-down" size={20} color={theme.accent} />
                </TouchableOpacity>
                {showRoomRequiredModal && (
                  <Text style={{ color: theme.error || '#d32f2f', fontWeight: '500', fontSize: 14, marginTop: 4, marginBottom: 2 }}>Room is required</Text>
                )}
                <FormLabel>Category *</FormLabel>
                <TouchableOpacity
                  ref={categoryFieldRef}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: theme.inputBackground,
                    borderColor: showCategoryRequiredError ? (theme.error || '#d32f2f') : theme.border,
                    borderWidth: 1,
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 0,
                    height: 44,
                  }}
                  onPress={() => setShowCategoryModal(true)}
                >
                  <Text style={{ color: selectedCategory ? theme.text : theme.secondaryText, fontSize: 16 }}>
                    {selectedCategory ? selectedCategory.name.replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.substr(1).toLowerCase()) : 'Select a category'}
                  </Text>
                  <Ionicons name="chevron-down" size={20} color={theme.accent} />
                </TouchableOpacity>
                {showCategoryRequiredError && (
                  <Text style={{ color: theme.error || '#d32f2f', fontWeight: '500', fontSize: 14, marginTop: 4, marginBottom: 2 }}>Category is required</Text>
                )}

                <FormLabel>Subcategory *</FormLabel>
                <TouchableOpacity
                  ref={subcategoryFieldRef}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: theme.inputBackground,
                    borderColor: showSubcategoryRequiredError ? (theme.error || '#d32f2f') : theme.border,
                    borderWidth: 1,
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 0,
                    height: 44,
                  }}
                  onPress={() => {
                    if (!selectedCategory) {
                      Alert.alert('Select category first');
                      return;
                    }
                    setShowSubcategoryModal(true);
                  }}
                >
                  <Text style={{ color: selectedSubcategory ? theme.text : theme.secondaryText, fontSize: 16 }}>
                    {selectedSubcategory ? selectedSubcategory.name.replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.substr(1).toLowerCase()) : 'Select a subcategory'}
                  </Text>
                  <Ionicons name="chevron-down" size={20} color={theme.accent} />
                </TouchableOpacity>
                {showSubcategoryRequiredError && (
                  <Text style={{ color: theme.error || '#d32f2f', fontWeight: '500', fontSize: 14, marginTop: 4, marginBottom: 2 }}>Subcategory is required</Text>
                )}
                {/* Dynamic attributes moved up below subcategory (exclude core fields) */}
                {attributeDefs.length > 0 ? (
                  <>
                    <Text style={{ color: theme.text, fontSize: 17, fontWeight: '500', marginBottom: 16, marginTop: 16 }}>Details</Text>
                    <DynamicAttributes
                      definitions={attributeDefs.filter((d) => !['make', 'model', 'serial', 'serial_number'].includes(d.key))}
                      values={dynAttributes}
                      onChange={setDynAttributes}
                    />
                  </>
                ) : null}
                {hasDef('make') && (
                  <>
                    <FormLabel>Make</FormLabel>
                    <View>
                      <BaseTextInput
                        style={{ backgroundColor: theme.inputBackground, color: theme.text, borderColor: theme.border, borderWidth: 1, borderRadius: 8, paddingVertical: 12, paddingHorizontal: 14, fontSize: 16 }}
                        value={make}
                        onChangeText={setMake}
                        placeholder="Enter manufacturer"
                        placeholderTextColor={theme.secondaryText}
                        maxLength={25}
                      />
                      <Text style={{ color: theme.secondaryText, fontSize: 12, textAlign: 'right', marginTop: 2 }}>{make.length}/25 characters</Text>
                    </View>
                  </>
                )}
                {hasDef('model') && (
                  <>
                    <FormLabel>Model</FormLabel>
                    <View>
                      <BaseTextInput
                        style={{ backgroundColor: theme.inputBackground, color: theme.text, borderColor: theme.border, borderWidth: 1, borderRadius: 8, paddingVertical: 12, paddingHorizontal: 14, fontSize: 16 }}
                        value={model}
                        onChangeText={setModel}
                        placeholder="Enter model number/name"
                        placeholderTextColor={theme.secondaryText}
                        maxLength={25}
                      />
                      <Text style={{ color: theme.secondaryText, fontSize: 12, textAlign: 'right', marginTop: 2 }}>{model.length}/25 characters</Text>
                    </View>
                  </>
                )}
                {hasDef('serial') && (
                  <>
                    <FormLabel>Serial Number</FormLabel>
                    <View>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        <BaseTextInput
                          style={{ backgroundColor: theme.inputBackground, color: theme.text, borderColor: theme.border, borderWidth: 1, borderRadius: 8, paddingVertical: 12, paddingHorizontal: 14, fontSize: 16, flex: 1 }}
                          value={serialNumber}
                          onChangeText={setSerialNumber}
                          placeholder="Enter serial number"
                          placeholderTextColor={theme.secondaryText}
                          autoCapitalize="none"
                          maxLength={25}
                        />
                        {Platform.OS !== 'web' && (
                          <TouchableOpacity
                            style={{ height: 44, width: 44, justifyContent: 'center', alignItems: 'center', backgroundColor: theme.inputBackground, borderRadius: 8, borderColor: theme.border, borderWidth: 1 }}
                            onPress={startScanning}
                          >
                            <Ionicons name="scan-outline" size={24} color={theme.accent} />
                          </TouchableOpacity>
                        )}
                      </View>
                      <Text style={{ color: theme.secondaryText, fontSize: 12, textAlign: 'right', marginTop: 2 }}>{serialNumber.length}/25 characters</Text>
                    </View>
                  </>
                )}
                <FormLabel>Description</FormLabel>
                <View>
                  <BaseTextInput
                    multiline
                    numberOfLines={9}
                    maxLength={600}
                    style={{ backgroundColor: theme.inputBackground, color: theme.text, borderColor: theme.border, borderWidth: 1, borderRadius: 8, paddingVertical: 12, paddingHorizontal: 14, fontSize: 16, minHeight: 80, textAlignVertical: 'top' }}
                    value={description}
                    onChangeText={setDescription}
                    placeholder="Enter item description"
                    placeholderTextColor={theme.secondaryText}
                  />
                  <Text style={{ color: theme.secondaryText, fontSize: 12, textAlign: 'right', marginTop: 2 }}>{description.length}/600 characters</Text>
                </View>
                
                
              </View>
              <View style={{ height: 1, backgroundColor: theme.border, marginVertical: 16 }} />
              
              {/* Purchase Info Section */}
              <Text style={{ color: theme.text, fontSize: 17, fontWeight: '500', marginBottom: 16, marginTop: 8 }}>Purchase Info</Text>
              <View style={{ gap: 12 }}>
                <FormLabel>Price ({currencySymbols[currency]})</FormLabel>
                <BaseTextInput
                  ref={priceFieldRef}
                  style={{
                    backgroundColor: theme.inputBackground,
                    color: theme.text,
                    borderColor: priceError ? (theme.error || '#d32f2f') : theme.border,
                    borderWidth: 1,
                    borderRadius: 8,
                    paddingVertical: 12,
                    paddingHorizontal: 14,
                    fontSize: 16
                  }}
                  value={price}
                  onChangeText={handlePriceChange}
                  placeholder="Enter price"
                  placeholderTextColor={theme.secondaryText}
                  keyboardType="numeric"
                  inputMode="decimal"
                />
                {priceError ? (
                  <Text style={{ color: theme.error || '#d32f2f', fontWeight: '500', fontSize: 14, marginTop: 4, marginBottom: 2 }}>
                    {priceError}
                  </Text>
                ) : null}
                <FormLabel>Purchase Date</FormLabel>
                <TouchableOpacity
                  style={{
                    backgroundColor: theme.inputBackground,
                    borderColor: theme.border,
                    borderWidth: 1,
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 0,
                    height: 44,
                    justifyContent: 'center'
                  }}
                  onPress={() => { setDatePickerMode('purchase'); setShowDatePicker(true); }}
                >
                  <Text style={{ color: purchaseDate ? theme.text : theme.secondaryText, fontSize: 16 }}>
                    {purchaseDate ? formatDate(purchaseDate) : 'Select date'}
                  </Text>
                </TouchableOpacity>
                <FormLabel>Place of Purchase</FormLabel>
                <View>
                  <BaseTextInput
                    style={{
                      backgroundColor: theme.inputBackground,
                      color: theme.text,
                      borderColor: theme.border,
                      borderWidth: 1,
                      borderRadius: 8,
                      paddingVertical: 12,
                      paddingHorizontal: 14,
                      fontSize: 16
                    }}
                    value={placeOfPurchase}
                    onChangeText={setPlaceOfPurchase}
                    placeholder="Enter place of purchase"
                    placeholderTextColor={theme.secondaryText}
                    maxLength={25}
                  />
                  <Text style={{ color: theme.secondaryText, fontSize: 12, textAlign: 'right', marginTop: 2 }}>{placeOfPurchase.length}/25 characters</Text>
                </View>
              </View>
              <View style={{ height: 1, backgroundColor: theme.border, marginVertical: 16 }} />
              {/* Warranty Info Section */}
              <Text style={{ color: theme.text, fontSize: 17, fontWeight: '500', marginBottom: 16, marginTop: 8 }}>Warranty Info</Text>
              <View style={{ gap: 12 }}>
                <FormLabel>Warranty Expiry</FormLabel>
                <TouchableOpacity
                  style={{ 
                    backgroundColor: theme.inputBackground, 
                    borderColor: theme.border, 
                    borderWidth: 1, 
                    borderRadius: 8, 
                    padding: 12, 
                    marginBottom: 0, 
                    height: 44, 
                    justifyContent: 'center' 
                  }}
                  onPress={() => { setDatePickerMode('warranty'); setShowWarrantyPicker(true); }}
                >
                  <Text style={{ color: warrantyExpiry ? theme.text : theme.secondaryText, fontSize: 16 }}>
                    {warrantyExpiry ? formatDate(warrantyExpiry) : 'Select date'}
                  </Text>
                </TouchableOpacity>
              </View>
              <View style={{ height: 1, backgroundColor: theme.border, marginVertical: 16 }} />
              {/* Documents and Images Section */}
              <Text style={{ color: theme.text, fontSize: 17, fontWeight: '500', marginBottom: 16, marginTop: 24 }}>Documents and Images</Text>
              <View style={{ gap: 16 }}>
                {/* Receipt */}
                <View style={{ marginBottom: 28 }}>
                  <FormLabel style={{ marginBottom: 8 }}>Receipt</FormLabel>
                  <ThemedButton
                    title={receiptFile ? 'Replace Receipt' : 'Upload Receipt'}
                    onPress={handleAddReceiptPress}
                    style={{ width: 220, alignSelf: 'flex-start' }}
                  />
                  {receiptFile || selectedReceipt ? (
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 10 }}>
                      <Ionicons name="document" size={18} color={theme.accent} />
                      <Text style={{ color: theme.text, fontSize: 14, marginLeft: 6 }}>
                        {receiptFile ? 'Receipt' : (selectedReceipt?.place_of_purchase || 'Receipt')}
                      </Text>
                      <TouchableOpacity onPress={() => {
                        setShowDeleteDocModal({ type: 'receipt' });
                        setPendingDeleteDoc(() => async () => {
                          if (receiptFile) {
                            // if user had just uploaded but not saved yet
                            setReceiptFile(null);
                          } else {
                            await removeLinkedReceipt();
                          }
                        });
                      }} style={{ marginLeft: 12 }}>
                        <Ionicons name="trash" size={22} color={theme.error || '#d32f2f'} />
                      </TouchableOpacity>
                    </View>
                  ) : null}
                </View>
                {/* Warranty Document */}
                <View style={{ marginBottom: 28 }}>
                  <FormLabel style={{ marginBottom: 8 }}>Warranty Document</FormLabel>
                  <ThemedButton
                    title={warrantyFile ? 'Replace Warranty' : 'Upload Warranty'}
                    onPress={() => setShowWarrantyModal(true)}
                    style={{ width: 220, alignSelf: 'flex-start' }}
                  />
                  {warrantyFile ? (
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 10 }}>
                      <Ionicons name="document" size={18} color={theme.accent} />
                      <Text style={{ color: theme.text, fontSize: 14, marginLeft: 6 }}>Warranty</Text>
                      <TouchableOpacity onPress={() => {
                        setShowDeleteDocModal({ type: 'warranty' });
                        setPendingDeleteDoc(() => async () => {
                          if (warrantyFile.file_path) {
                            await supabase.storage.from('item-documents').remove([warrantyFile.file_path]);
                            await supabase.from('item_documents').delete().eq('item_id', itemId).eq('type', 'warranty');
                            setWarrantyFile(null);
                          }
                        });
                      }} style={{ marginLeft: 12 }}>
                        <Ionicons name="trash" size={22} color={theme.error || '#d32f2f'} />
                      </TouchableOpacity>
                    </View>
                  ) : null}
                </View>

                {/* Additional Documents */}
                <View style={{ marginBottom: 28 }}>
                  <FormLabel style={{ marginBottom: 8 }}>Additional Documents (Max. {MAX_ADDITIONAL_DOCS})</FormLabel>
                  {/* Existing docs */}
                  {existingAdditionalDocs.length > 0 ? (
                    <View style={{ gap: 8 }}>
                      {existingAdditionalDocs.map((d) => (
                        <View key={d.id} style={{ flexDirection: 'row', alignItems: 'center' }}>
                          <Ionicons name="document-text" size={18} color={theme.accent} />
                          <Text style={{ color: theme.text, fontSize: 14, marginLeft: 6, flex: 1 }} numberOfLines={1}>{d.name || 'Document'}</Text>
                          <TouchableOpacity
                            onPress={() => setRenameDocModal({ id: d.id, name: d.name || '' })}
                            style={{ marginLeft: 8 }}
                            accessibilityLabel="Rename document"
                          >
                            <Ionicons name="pencil" size={18} color={theme.accent} />
                          </TouchableOpacity>
                          <TouchableOpacity onPress={() => removeExistingAdditionalDocument(d.id, d.file_path)} style={{ marginLeft: 12 }}>
                            <Ionicons name="trash" size={20} color={theme.error || '#d32f2f'} />
                          </TouchableOpacity>
                        </View>
                      ))}
                    </View>
                  ) : null}
                  {/* Pending docs */}
                  {pendingAdditionalDocs.length > 0 ? (
                    <View style={{ gap: 8, marginTop: 6 }}>
                      {pendingAdditionalDocs.map((d, idx) => (
                        <View key={`${d.uri}-${idx}`} style={{ flexDirection: 'row', alignItems: 'center' }}>
                          <Ionicons name="document-attach" size={18} color={theme.accent} />
                          <Text style={{ color: theme.text, fontSize: 14, marginLeft: 6, flex: 1 }} numberOfLines={1}>{d.name}</Text>
                          <TouchableOpacity onPress={() => removePendingAdditionalDocumentAt(idx)} style={{ marginLeft: 12 }}>
                            <Ionicons name="trash" size={20} color={theme.error || '#d32f2f'} />
                          </TouchableOpacity>
                        </View>
                      ))}
                    </View>
                  ) : (existingAdditionalDocs.length === 0 ? (
                    <Text style={{ color: theme.secondaryText, fontSize: 14, marginBottom: 8 }}>No additional documents</Text>
                  ) : null)}
                  <ThemedButton
                    title={(existingAdditionalDocs.length + pendingAdditionalDocs.length) >= MAX_ADDITIONAL_DOCS ? 'Limit Reached' : 'Add Document'}
                    onPress={handlePickAdditionalDocument}
                    disabled={(existingAdditionalDocs.length + pendingAdditionalDocs.length) >= MAX_ADDITIONAL_DOCS}
                    style={{ width: 220, alignSelf: 'flex-start', marginTop: 8 }}
                  />
                </View>
                {/* Item Pictures */}
                <FormLabel style={{ marginBottom: 8 }}>Item Pictures (Max. 3)</FormLabel>
                {showImageLimitError && (
                  <Text style={{ color: theme.error || '#d32f2f', fontWeight: '500', fontSize: 14, marginBottom: 8 }}>
                    Maximum of 3 images per item, remove additional images
                  </Text>
                )}
                {Platform.OS === 'web' ? (
                  <WebImageGridComponent
                    pictures={pictures}
                    onReorder={handleDragEnd}
                    onDelete={webOnDeletePicture}
                    onAdd={webOnAddPicture}
                    size={imageSquareSize}
                  />
                ) : (
                  <NativeImageGridComponent
                    pictures={pictures}
                    onReorder={handleDragEnd}
                    onDelete={(id) => {
                      const index = pictures.findIndex(p => p.id === id);
                      if (index !== -1) {
                        handleDeletePicture(index);
                      }
                    }}
                    onAdd={handlePickItemPictures}
                    size={imageSquareSize}
                    theme={{ inputBackground: theme.inputBackground, border: theme.border, card: theme.card, text: theme.text }}
                  />
                )}
              </View>
              {/* Save Changes Button */}
              <ThemedButton
                title={loading ? 'Saving...' : 'Save Changes'}
                onPress={handleSave}
                disabled={loading}
                style={{ marginTop: 24 }}
                accessibilityLabel="Save Changes"
              />
              {/* Delete Item Button */}
              <ThemedButton
                title="Delete Item"
                variant="danger"
                onPress={() => setShowDeleteModal(true)}
                style={{ marginTop: 16 }}
                accessibilityLabel="Delete Item"
              />
            </View>
          </View>
        </ScrollView>
        {renderRoomModal()}
        {renderDatePicker()}
        {renderCategoryModal()}
        {renderSubcategoryModal()}
      {/* Add Additional Document - Name Modal */}
      <Modal
        visible={showAddDocNameModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowAddDocNameModal(false)}
      >
        <View style={{ flex: 1, backgroundColor: theme.modalOverlay, justifyContent: 'center', alignItems: 'center' }}>
          <View style={{ backgroundColor: theme.background, borderRadius: 12, padding: 24, width: 320 }}>
            <Text style={[styles.modalTitle, { color: theme.text, marginBottom: 12 }]}>Name Document</Text>
            <BaseTextInput
              style={{ backgroundColor: theme.inputBackground, color: theme.text, borderColor: theme.border, borderWidth: 1, borderRadius: 8, paddingVertical: 10, paddingHorizontal: 12, fontSize: 16, marginBottom: 12 }}
              placeholder="e.g. Jewellery valuation"
              placeholderTextColor={theme.secondaryText}
              value={pendingAdditionalDocName}
              onChangeText={setPendingAdditionalDocName}
              maxLength={60}
            />
            <View style={{ flexDirection: 'row', gap: 12 }}>
              <ThemedButton title="Cancel" variant="secondary" onPress={() => { setShowAddDocNameModal(false); setPendingAdditionalDocFile(null); }} style={{ flex: 1 }} />
              <ThemedButton title="Add" onPress={confirmAddAdditionalDocument} style={{ flex: 1 }} />
            </View>
          </View>
        </View>
      </Modal>
      {/* Rename Additional Document Modal */}
      <Modal
        visible={!!renameDocModal.id}
        transparent
        animationType="fade"
        onRequestClose={() => setRenameDocModal({ id: null, name: '' })}
      >
        <View style={{ flex: 1, backgroundColor: theme.modalOverlay, justifyContent: 'center', alignItems: 'center' }}>
          <View style={{ backgroundColor: theme.background, borderRadius: 12, padding: 24, width: 320 }}>
            <Text style={[styles.modalTitle, { color: theme.text, marginBottom: 12 }]}>Rename Document</Text>
            <BaseTextInput
              style={{ backgroundColor: theme.inputBackground, color: theme.text, borderColor: theme.border, borderWidth: 1, borderRadius: 8, paddingVertical: 10, paddingHorizontal: 12, fontSize: 16, marginBottom: 12 }}
              placeholder="New name"
              placeholderTextColor={theme.secondaryText}
              value={renameDocModal.name}
              onChangeText={(t: string) => setRenameDocModal(prev => ({ ...prev, name: t }))}
              maxLength={60}
            />
            <View style={{ flexDirection: 'row', gap: 12 }}>
              <ThemedButton title="Cancel" variant="secondary" onPress={() => setRenameDocModal({ id: null, name: '' })} style={{ flex: 1 }} />
              <ThemedButton title="Save" onPress={submitRenameAdditionalDocument} style={{ flex: 1 }} />
            </View>
          </View>
        </View>
      </Modal>
        {/* Suggest Category Modal */}
        <Modal
          visible={showSuggestCategoryModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowSuggestCategoryModal(false)}
        >
          <View style={{ flex: 1, backgroundColor: theme.modalOverlay, justifyContent: 'center', alignItems: 'center' }}>
            <View style={{ backgroundColor: theme.background, borderRadius: 12, padding: 24, width: 320 }}>
              <Text style={[styles.modalTitle, { color: theme.text, marginBottom: 12 }]}>Suggest New Category</Text>
              <BaseTextInput
                style={{ backgroundColor: theme.inputBackground, color: theme.text, borderColor: theme.border, borderWidth: 1, borderRadius: 8, paddingVertical: 10, paddingHorizontal: 12, fontSize: 16, marginBottom: 12 }}
                placeholder="Category name"
                placeholderTextColor={theme.secondaryText}
                value={proposedCategoryName}
                onChangeText={setProposedCategoryName}
              />
              <View style={{ flexDirection: 'row', gap: 12 }}>
                <ThemedButton title="Cancel" variant="secondary" onPress={() => setShowSuggestCategoryModal(false)} style={{ flex: 1 }} />
                <ThemedButton title={suggestLoading ? 'Submitting...' : 'Submit'} onPress={submitCategorySuggestion} disabled={suggestLoading} style={{ flex: 1 }} />
              </View>
            </View>
          </View>
        </Modal>
        {/* Suggest Subcategory Modal */}
        <Modal
          visible={showSuggestSubcategoryModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowSuggestSubcategoryModal(false)}
        >
          <View style={{ flex: 1, backgroundColor: theme.modalOverlay, justifyContent: 'center', alignItems: 'center' }}>
            <View style={{ backgroundColor: theme.background, borderRadius: 12, padding: 24, width: 320 }}>
              <Text style={[styles.modalTitle, { color: theme.text, marginBottom: 12 }]}>Suggest New Subcategory</Text>
              {selectedCategory ? (
                <Text style={{ color: theme.secondaryText, marginBottom: 6 }}>For: {selectedCategory.name}</Text>
              ) : null}
              <BaseTextInput
                style={{ backgroundColor: theme.inputBackground, color: theme.text, borderColor: theme.border, borderWidth: 1, borderRadius: 8, paddingVertical: 10, paddingHorizontal: 12, fontSize: 16, marginBottom: 12 }}
                placeholder="Subcategory name"
                placeholderTextColor={theme.secondaryText}
                value={proposedSubcategoryName}
                onChangeText={setProposedSubcategoryName}
              />
              <View style={{ flexDirection: 'row', gap: 12 }}>
                <ThemedButton title="Cancel" variant="secondary" onPress={() => setShowSuggestSubcategoryModal(false)} style={{ flex: 1 }} />
                <ThemedButton title={suggestLoading ? 'Submitting...' : 'Submit'} onPress={submitSubcategorySuggestion} disabled={suggestLoading || !selectedCategory} style={{ flex: 1 }} />
              </View>
            </View>
          </View>
        </Modal>
        {/* Delete Document Confirmation Modal */}
        <Modal
          visible={!!showDeleteDocModal.type}
          transparent
          animationType="fade"
          onRequestClose={() => setShowDeleteDocModal({ type: null })}
        >
          <View style={[styles.modalOverlay, { backgroundColor: 'rgba(0, 0, 0, 0.5)' }]}> 
            <View style={[styles.deleteModalContent, { backgroundColor: theme.card, alignItems: 'center' }]}> 
              <Text style={[styles.deleteModalTitle, { color: theme.text, textAlign: 'center', marginBottom: 12 }]}>Delete {showDeleteDocModal.type === 'receipt' ? 'Receipt' : 'Warranty'} Document</Text> 
              <Text style={[styles.deleteModalText, { color: theme.text, textAlign: 'center', marginBottom: 20 }]}>Are you sure you want to delete this {showDeleteDocModal.type} document?</Text> 
              <View style={[styles.deleteModalButtons, { gap: 12, marginTop: 8, justifyContent: 'center', width: '100%' }]}> 
                <TouchableOpacity
                  style={[styles.deleteModalButton, styles.cancelButton, { minWidth: 100, paddingVertical: 8, paddingHorizontal: 16, backgroundColor: theme.accent }]}
                  onPress={() => setShowDeleteDocModal({ type: null })}
                >
                  <Text style={[styles.cancelButtonText, { color: theme.buttonText, textAlign: 'center' }]}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.deleteModalButton, styles.deleteButton, { minWidth: 100, paddingVertical: 8, paddingHorizontal: 16, backgroundColor: theme.accent }]}
                  onPress={async () => {
                    if (pendingDeleteDoc) await pendingDeleteDoc();
                    setShowDeleteDocModal({ type: null });
                  }}
                >
                  <Text style={[styles.deleteButtonText, { color: theme.buttonText, textAlign: 'center' }]}>Delete</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
        <Modal
          visible={showReceiptModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowReceiptModal(false)}
        >
          <View style={{ flex: 1, backgroundColor: theme.modalOverlay, justifyContent: 'center', alignItems: 'center' }}>
            <View style={{ backgroundColor: theme.background, borderRadius: 12, padding: 24, width: 300, alignItems: 'center' }}>
              <Text style={[styles.modalTitle, { color: theme.text, marginBottom: 16 }]}>Add Receipt</Text>
              <ThemedButton
                title="Image"
                onPress={async () => { setShowReceiptModal(false); await handlePickImage('receipt'); }}
                style={{ width: '100%', marginBottom: 16 }}
              />
              <ThemedButton
                title="PDF"
                onPress={async () => { setShowReceiptModal(false); await handlePickDocument('receipt'); }}
                style={{ width: '100%', marginBottom: 16 }}
              />
              <ThemedButton
                title="Select Existing"
                onPress={() => { setShowReceiptModal(false); setShowReceiptSelector(true); }}
                style={{ width: '100%', marginBottom: 16 }}
              />
              <ThemedButton
                title="Cancel"
                variant="secondary"
                onPress={() => setShowReceiptModal(false)}
                style={{ width: '100%' }}
              />
            </View>
          </View>
        </Modal>
        <Modal
          visible={showWarrantyModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowWarrantyModal(false)}
        >
          <View style={{ flex: 1, backgroundColor: theme.modalOverlay, justifyContent: 'center', alignItems: 'center' }}>
            <View style={{ backgroundColor: theme.background, borderRadius: 12, padding: 24, width: 300, alignItems: 'center' }}>
              <Text style={[styles.modalTitle, { color: theme.text, marginBottom: 16 }]}>Add Warranty</Text>
              <ThemedButton
                title="Image"
                onPress={async () => { setShowWarrantyModal(false); await handlePickImage('warranty'); }}
                style={{ width: '100%', marginBottom: 16 }}
              />
              <ThemedButton
                title="PDF"
                onPress={async () => { setShowWarrantyModal(false); await handlePickDocument('warranty'); }}
                style={{ width: '100%', marginBottom: 16 }}
              />
              <ThemedButton
                title="Cancel"
                variant="secondary"
                onPress={() => setShowWarrantyModal(false)}
                style={{ width: '100%' }}
              />
            </View>
          </View>
        </Modal>
        <Modal
          visible={showReceiptAdviceModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowReceiptAdviceModal(false)}
        >
          <View style={{ flex: 1, backgroundColor: theme.modalOverlay, justifyContent: 'center', alignItems: 'center' }}>
            <View style={{ backgroundColor: theme.background, borderRadius: 12, padding: 24, width: 300, alignItems: 'center' }}>
              <Text style={[styles.modalTitle, { color: theme.text, marginBottom: 16 }]}>Add Purchase Details</Text>
              <Text style={{ color: theme.text, fontSize: 16, textAlign: 'center', marginBottom: 24 }}>{receiptAdviceMessage}</Text>
              <View style={{ width: '100%', gap: 12 }}>
                <ThemedButton
                  title="Ignore and Continue"
                  variant="secondary"
                  onPress={() => {
                    setShowReceiptAdviceModal(false);
                    setShowReceiptModal(true);
                  }}
                  style={{ width: '100%' }}
                />
                <ThemedButton
                  title="Go Back"
                  variant="primary"
                  onPress={() => setShowReceiptAdviceModal(false)}
                  style={{ width: '100%' }}
                />
              </View>
            </View>
          </View>
        </Modal>
        {/* Delete item modal */}
        <Modal
          visible={showDeleteModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowDeleteModal(false)}
        >
          <View style={[styles.modalOverlay, { backgroundColor: theme.background }]}> 
            <View style={[styles.deleteModalContent, { backgroundColor: theme.card }]}> 
              <Text style={[styles.deleteModalTitle, { color: theme.text }]}>Delete Item</Text> 
              <Text style={[styles.deleteModalText, { color: theme.text }]}>Are you sure you want to delete this item?{"\n\n"}This will also remove any images, receipts (if not used by other items) and warranty documents for this item.</Text> 
              <View style={[styles.deleteModalButtons, { gap: 12 }]}> 
                <TouchableOpacity
                  style={[styles.deleteModalButton, styles.cancelButton, { backgroundColor: theme.accent }]}
                  onPress={() => setShowDeleteModal(false)}
                >
                  <Text style={[styles.cancelButtonText, { color: theme.buttonText }]}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.deleteModalButton, styles.deleteButton]}
                  onPress={confirmDeleteItem}
                >
                  <Text style={styles.deleteButtonText}>Delete</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
      </KeyboardAvoidingView>
      {/* Receipt selector modal */}
      <ReceiptSelector
        visible={showReceiptSelector}
        filterPlace={placeOfPurchase || null}
        onClose={() => setShowReceiptSelector(false)}
        onSelect={(r) => {
          setSelectedReceipt(r);
          setReceiptFile(null);
          setShowReceiptSelector(false);
        }}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#1a1a1a',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#1a1a1a',
  },
  scrollContent: {
    flexGrow: 1,
  },
  header: {
    marginBottom: 30,
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#333',
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 0,
  },
  backButton: {
    padding: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 6,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#34C759',
  },
  content: {
    padding: 20,
  },
  webContent: {
    maxWidth: 1200,
    marginHorizontal: 'auto',
    width: '100%',
  },
  form: {
    width: '100%',
  },
  webForm: {
    maxWidth: 1200,
    marginHorizontal: 'auto',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 32,
    alignItems: 'flex-start',
  },
  formColumn: {
    width: '100%',
    marginBottom: 16,
  },
  webFormColumn: {
    flex: 1,
    minWidth: 300,
    maxWidth: 400,
  },
  label: {
    color: '#fff',
    fontSize: 16,
    marginBottom: 8,
    marginTop: 0,
  },
  input: {
    backgroundColor: '#2a2a2a',
    borderRadius: 8,
    padding: 12,
    color: '#fff',
    fontSize: 16,
    borderWidth: 1,
    borderColor: '#333',
    width: '100%',
    marginBottom: 16,
    height: 44,
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
    paddingTop: 12,
  },
  dateButton: {
    backgroundColor: '#2a2a2a',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#333',
    marginBottom: 16,
    height: 44,
  },
  dateButtonText: {
    color: '#fff',
    fontSize: 16,
  },
  fileButtons: {
    flexDirection: 'row',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  fileButton: {
    flex: 1,
    minWidth: 150,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#2a2a2a',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#333',
  },
  fileButtonText: {
    color: '#007AFF',
    fontSize: 16,
  },
  fileName: {
    color: '#666',
    fontSize: 14,
    marginTop: 4,
  },
  roomSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#2a2a2a',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#333',
    marginBottom: 16,
    height: 44,
  },
  roomSelectorText: {
    color: '#fff',
    fontSize: 16,
  },
  addButton: {
    backgroundColor: '#007AFF',
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
    width: '100%',
    maxWidth: 400,
    height: 44,
    justifyContent: 'center',
  },
  disabledButton: {
    opacity: 0.5,
  },
  addButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#2a2a2a',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 17,
    fontWeight: '500',
    color: '#fff',
  },
  closeButton: {
    padding: 8,
  },
  roomOption: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#333',
  },
  selectedRoom: {
    backgroundColor: '#007AFF20',
  },
  roomOptionText: {
    color: '#fff',
    fontSize: 16,
  },
  selectedRoomText: {
    color: '#007AFF',
  },
  datePickerContainer: {
    padding: 20,
    gap: 16,
  },
  confirmButton: {
    backgroundColor: '#007AFF',
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
  },
  confirmButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  picturesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 16,
  },
  pictureWrapper: {
    position: 'relative',
  },
  picture: {
    width: 100,
    height: 100,
    borderRadius: 8,
  },
  deletePictureButton: {
    position: 'absolute',
    top: -8,
    right: -8,
    backgroundColor: '#fff',
    borderRadius: 12,
  },
  addPictureButton: {
    width: 100,
    height: 100,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#007AFF',
    borderStyle: 'dashed',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
  },
  addPictureText: {
    color: '#007AFF',
    fontSize: 12,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  datePickerModalContent: {
    maxWidth: 400,
    width: '90%',
    borderRadius: 12,
    padding: 24,
  },
  datePickerButtons: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
    maxWidth: 300,
  },
  datePickerButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelDateButton: {
    backgroundColor: '#333',
  },
  confirmDateButton: {
    backgroundColor: '#007AFF',
  },
  datePickerButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  serialNumberContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  },
  serialNumberInput: {
    flex: 1,
    marginBottom: 0,
    height: 44,
  },
  scanButton: {
    height: 44,
    width: 44,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#2a2a2a',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#333',
  },
  scannerContainer: {
    flex: 1,
    backgroundColor: '#000',
  },
  scannerOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  scannerText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'center',
    backgroundColor: 'rgba(0,0,0,0.7)',
    padding: 16,
    borderRadius: 8,
  },
  deleteModalContent: {
    padding: 20,
    borderRadius: 12,
    maxWidth: 400,
    width: '80%',
    alignItems: 'center',
    backgroundColor: '#2a2a2a',
  },
  deleteModalTitle: {
    fontSize: 17,
    fontWeight: '500',
    color: '#fff',
    marginBottom: 12,
    textAlign: 'center',
  },
  deleteModalText: {
    color: '#fff',
    fontSize: 16,
    marginBottom: 20,
    textAlign: 'center',
  },
  deleteModalButtons: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 12,
    width: '100%',
  },
  deleteModalButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
    minWidth: 100,
    backgroundColor: '#007AFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButton: {
    backgroundColor: '#333',
  },
  cancelButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  deleteButton: {
    backgroundColor: '#d32f2f',
  },
  deleteButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
}); 